import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Shimmer } from '@teals/ui-web';
import {
  useGetDashboardQuery,
  useGetAgentBreakdownQuery,
  useGetTaskAnalyticsQuery,
} from '../../store/api';

type RangeKey = '24h' | '7d' | '30d' | 'all';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
];

const toDateParams = (key: RangeKey) => {
  if (key === 'all') return {};
  const end = new Date();
  const start = new Date();
  if (key === '24h') start.setDate(start.getDate() - 1);
  else if (key === '7d') start.setDate(start.getDate() - 7);
  else start.setDate(start.getDate() - 30);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

const PageBody = styled.div`
  padding: 1.5rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 { font-size: 1.5rem; margin-bottom: 0.25rem; }
  p { font-size: 0.875rem; color: var(--text-muted); }
`;

const PillBar = styled.div`
  display: flex;
  gap: 0.375rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.25rem;
`;

const Pill = styled.button<{ $active?: boolean }>`
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-elevated)')};
  }
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const KPIStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const KPICard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
  transition: box-shadow 0.2s;

  &:hover { box-shadow: var(--shadow-md); }
`;

const KPILabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const KPIValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
`;

const TableSection = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th, td {
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  th {
    font-weight: 600;
    color: var(--text-muted);
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td { color: var(--text-primary); }
  tbody tr:hover { background: var(--bg-elevated); }
  tbody tr:last-child td { border-bottom: none; }
`;

const TaskKPIs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const FailingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FailingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const FailName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const FailMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FailCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--error);
  background: var(--error-subtle);
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
`;

const FailError = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShimmerRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const formatNum = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

const Analytics: React.FC = () => {
  const [range, setRange] = useState<RangeKey>('7d');
  const params = useMemo(() => toDateParams(range), [range]);

  const { data: dashboard, isLoading: dashLoading } = useGetDashboardQuery(params);
  const { data: agentData, isLoading: agentsLoading } = useGetAgentBreakdownQuery(params);
  const { data: tasks, isLoading: tasksLoading } = useGetTaskAnalyticsQuery(params);

  const agents = agentData?.agents ?? [];

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <h1>Analytics</h1>
            <p>Monitor agent performance and platform health</p>
          </PageHeaderLeft>
          <PillBar>
            {RANGES.map((r) => (
              <Pill key={r.key} $active={range === r.key} onClick={() => setRange(r.key)}>
                {r.label}
              </Pill>
            ))}
          </PillBar>
        </PageHeader>

        <SectionTitle>Dashboard</SectionTitle>
        <KPIStrip>
          {dashLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <KPICard key={i}>
                <Shimmer width="60%" height={12} borderRadius={4} />
                <div style={{ marginTop: '0.75rem' }}>
                  <Shimmer width="50%" height={28} borderRadius={6} />
                </div>
              </KPICard>
            ))
          ) : (
            <>
              <KPICard>
                <KPILabel>Total Sessions</KPILabel>
                <KPIValue>{formatNum(dashboard?.totalSessions ?? 0)}</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Active Agents</KPILabel>
                <KPIValue>{dashboard?.activeAgents ?? 0}</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Avg Response Time</KPILabel>
                <KPIValue>{((dashboard?.avgResponseTime ?? 0) / 1000).toFixed(1)}s</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Success Rate</KPILabel>
                <KPIValue>{(dashboard?.successRate ?? 0).toFixed(1)}%</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Tokens Used</KPILabel>
                <KPIValue>{formatNum(dashboard?.tokensUsed ?? 0)}</KPIValue>
              </KPICard>
            </>
          )}
        </KPIStrip>

        <SectionTitle>Agent Breakdown</SectionTitle>
        <TableSection>
          {agentsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <ShimmerRow key={i}>
                <Shimmer width="100%" height={18} borderRadius={4} />
              </ShimmerRow>
            ))
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Sessions</th>
                  <th>Success Rate</th>
                  <th>Avg Response</th>
                  <th>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.agentId}>
                    <td>{a.displayName ?? a.agentName}</td>
                    <td>{formatNum(a.sessionCount)}</td>
                    <td>{a.successRate.toFixed(1)}%</td>
                    <td>{(a.avgResponseTime / 1000).toFixed(1)}s</td>
                    <td>{formatNum(a.tokensUsed)}</td>
                  </tr>
                ))}
                {agents.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No agent data</td></tr>
                )}
              </tbody>
            </StyledTable>
          )}
        </TableSection>

        <SectionTitle>Task Analytics</SectionTitle>
        {tasksLoading ? (
          <TaskKPIs>
            {Array.from({ length: 3 }).map((_, i) => (
              <KPICard key={i}>
                <Shimmer width="60%" height={12} borderRadius={4} />
                <div style={{ marginTop: '0.75rem' }}>
                  <Shimmer width="50%" height={28} borderRadius={6} />
                </div>
              </KPICard>
            ))}
          </TaskKPIs>
        ) : (
          <>
            <TaskKPIs>
              <KPICard>
                <KPILabel>Total Tasks</KPILabel>
                <KPIValue>{formatNum(tasks?.totalTasks ?? 0)}</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Task Success Rate</KPILabel>
                <KPIValue>{(tasks?.successRate ?? 0).toFixed(1)}%</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Avg Duration</KPILabel>
                <KPIValue>{((tasks?.avgDuration ?? 0) / 1000).toFixed(1)}s</KPIValue>
              </KPICard>
            </TaskKPIs>

            {(tasks?.topFailingTasks?.length ?? 0) > 0 && (
              <>
                <SectionTitle>Top Failing Tasks</SectionTitle>
                <FailingList>
                  {tasks!.topFailingTasks.map((t) => (
                    <FailingItem key={t.taskName}>
                      <FailName>{t.taskName}</FailName>
                      <FailMeta>
                        <FailError title={t.lastError}>{t.lastError}</FailError>
                        <FailCount>{t.failureCount} failures</FailCount>
                      </FailMeta>
                    </FailingItem>
                  ))}
                </FailingList>
              </>
            )}
          </>
        )}
      </Container>
    </PageBody>
  );
};

export default Analytics;
